import { DetailAction } from './DetailAction';
import { AssetType, StreamType } from '../../../api/models/Shared.types';
import { DetailTranslationKeys, ErrorTranslationKeys } from '../../../api/models/TranslationKeys';
import { ReplayStreamInfo } from '../../player/streamInfo/ReplayStreamInfo';
import { LinearStreamInfo } from '../../player/streamInfo/LinearStreamInfo';
import { NpvrStreamInfo } from '../../player/streamInfo/NpvrStreamInfo';
import { SelectOptionDialog } from '../../../components/option/SelectOptionDialog';
import { EpgEvent, StartPositionType } from '../../player/streamInfo/StreamInfo';
import { Backend } from '../../../api/Backend';
import { ChannelModel } from '../../../api/EpgApi.types';
import {
  IBookmarkElementV1,
  IProgramSummary,
  IScheduleDetail,
  ISeasonSummary,
  ISeriesSummary,
} from '../../../api/models/AndromeTV.types';
import { Recording } from '../../../api/user/RecordingApi.types';
import { OptionData } from '../../../components/option/OptionData';
import { hasLinearRights, hasRecordingRights, hasReplayRights } from '../epg/EpgWatchability';
import { EpgUtil } from '../../utility/EpgUtil';
import { BlackoutUtil } from '../../utility/BlackoutUtil';
import { Router } from '@lightningjs/sdk';
import { PlayerPage } from '../../../pages/PlayerPage';

const WatchOptions = {
  replay: 'replay',
  replayFromBookmark: 'replayFromBookmark',
  live: 'live',
  recording: 'recording',
  recordingFromBookmark: 'recordingFromBookmark',
};

const optionTexts = {
  [WatchOptions.replay]: DetailTranslationKeys.detail_watch_from_beginning,
  [WatchOptions.replayFromBookmark]: DetailTranslationKeys.detail_watch_from_bookmark,
  [WatchOptions.live]: DetailTranslationKeys.detail_watch_live,
  [WatchOptions.recording]: DetailTranslationKeys.detail_watch_from_beginning,
  [WatchOptions.recordingFromBookmark]: DetailTranslationKeys.detail_watch_from_bookmark,
};

export class EpgWatchAction extends DetailAction {
  private type: AssetType | undefined;
  private channel: ChannelModel;
  private schedule: IScheduleDetail;
  private program: IProgramSummary;
  private series: ISeriesSummary | undefined;
  private season: ISeasonSummary | undefined;
  private recording: Recording | undefined;
  private adult: boolean | undefined;
  private channelsList: ChannelModel[];
  private isSeriesDetail: boolean;

  constructor(
    backend: Backend,
    type: AssetType | undefined,
    channel: ChannelModel,
    schedule: IScheduleDetail,
    program: IProgramSummary,
    series: ISeriesSummary | undefined,
    season: ISeasonSummary | undefined,
    recording: Recording | undefined,
    adult: boolean | undefined,
    channelsList: ChannelModel[],
    isSeriesDetail = false
  ) {
    super(backend);
    this.type = type;
    this.channel = channel;
    this.schedule = schedule;
    this.program = program;
    this.series = series;
    this.season = season;
    this.recording = recording;
    this.adult = adult;
    this.channelsList = channelsList;
    this.isSeriesDetail = isSeriesDetail;
  }

  getOptions() {
    return this._backend.cachedBookmarkApi
      .bookmarkShortCache(this._backend, {
        type: AssetType.PROGRAM,
        id: this.recording ? this.recording.program : this.schedule.program,
        profileId: this._backend.deviceProfile?.id,
      })
      .then(bookmark => {
        return this.getWatchOptions(bookmark);
      });
  }

  override doAction() {
    this._backend.cachedBookmarkApi
      .bookmarkShortCache(this._backend, {
        type: AssetType.PROGRAM,
        id: this.recording ? this.recording.program : this.schedule.program,
        profileId: this._backend.deviceProfile?.id,
      })
      .then(bookmark => {
        return this.getWatchOptions(bookmark);
      })
      .then(watchOptions => {
        const options: any[] = watchOptions;
        if (options.length === 0) {
          this.owner?.fireAncestors(
            '$showMessage',
            this._backend.translations.forKey(ErrorTranslationKeys.error_blackout)
          );
          return;
        }
        if (options.length === 1) {
          this.optionSelected({ value: options[0], options: [] });
          return;
        }
        this.owner?.fireAncestors('$openDialog', {
          type: SelectOptionDialog,
          params: {
            backend: this._backend,
            title: this._backend.translations
              .forKey(DetailTranslationKeys.detail_watch_popup_title)
              .replace('$1', this.program.title ? this.program.title : ''),
            options: options.map(o => ({
              text: this._backend.translations.forKey(optionTexts[o.option]!),
              value: o,
            })),
            onSuccess: (val: OptionData) => {
              this.hideOptions();
              this.optionSelected(val);
            },
            onCancelled: this.hideOptions.bind(this),
          },
        });
      });
  }

  optionSelected(option: OptionData) {
    const epgEvent: EpgEvent = {
      channel: this.channel,
      program: this.program,
      season: this.season,
      series: this.series,
      schedule: this.schedule,
      recording: this.recording,
    };
    let streamInfo;
    switch (option.value.option) {
      case WatchOptions.replay:
        streamInfo = new ReplayStreamInfo(epgEvent, StartPositionType.START, this._backend, option.value.bookmark);
        break;
      case WatchOptions.replayFromBookmark:
        streamInfo = new ReplayStreamInfo(epgEvent, StartPositionType.BOOKMARK, this._backend, option.value.bookmark);
        break;
      case WatchOptions.live:
        streamInfo = new LinearStreamInfo(epgEvent, this._backend, hasReplayRights(this.schedule, this.channel));
        break;
      case WatchOptions.recording:
        streamInfo = new NpvrStreamInfo(epgEvent, option.value.bookmark, StartPositionType.START, this._backend);
        break;
      case WatchOptions.recordingFromBookmark:
        streamInfo = new NpvrStreamInfo(epgEvent, option.value.bookmark, StartPositionType.BOOKMARK, this._backend);
        break;
    }
    if (streamInfo) {
      if (Router.getActiveHash() === 'play') {
        (Router.getActivePage() as PlayerPage).closePlayerModals();
        this._backend.playerController.changeStream(streamInfo);
      } else {
        Router.navigate('play', { streamInfo });
      }
      return;
    }
    this.hideOptions();
  }

  hideOptions() {
    this.owner?.fireAncestors('$closeDialog');
  }

  getWatchOptions(bookmark: IBookmarkElementV1 | undefined): any[] {
    const options = [];
    if (
      this.type !== AssetType.RECORDING &&
      hasLinearRights(this.schedule, this.channel) &&
      !this.isSeriesDetail &&
      !EpgUtil.scheduleHasBlackout(this.schedule?.blackouts, BlackoutUtil.create(StreamType.LINEAR))
    ) {
      options.push({ option: WatchOptions.live });
    }
    if (
      this.type !== AssetType.RECORDING &&
      hasReplayRights(this.schedule, this.channel) &&
      !EpgUtil.scheduleHasBlackout(this.schedule?.blackouts, BlackoutUtil.create(StreamType.REPLAY))
    ) {
      if (bookmark) {
        options.push({ option: WatchOptions.replayFromBookmark, bookmark });
      }
      if (!this.isSeriesDetail || !bookmark) {
        options.push({
          option: WatchOptions.replay,
        });
      }
    } else if (
      hasRecordingRights(this.schedule, this.channel, this.recording, this._backend) &&
      !EpgUtil.scheduleHasBlackout(this.schedule?.blackouts, BlackoutUtil.create(StreamType.NPVR))
    ) {
      if (bookmark) {
        options.push({
          option: WatchOptions.recordingFromBookmark,
          bookmark: bookmark,
        });
      }
      if (!this.isSeriesDetail || !bookmark) {
        options.push({
          option: WatchOptions.recording,
        });
      }
    }
    return options;
  }
}
